import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BlockSchema, I18n, JSONSchemaNode } from '@/lib/api/types';
import { resolveFields, emptyValue, mapValueSchema, type ResolvedField } from './resolveSchema';
import { I18nField } from './widgets/I18nField';
import { MediaPickerField, EntityPickerField } from './widgets/PickerField';
import { ReferenceSelectField } from './widgets/ReferenceSelectField';
import { GeoPickerField } from './widgets/GeoPickerField';
import { TextInput, TextArea } from '@/lib/ui/Field';
import { Select } from '@/lib/ui/Select';
import { Switch } from '@/lib/ui/Switch';
import { Button } from '@/lib/ui/Button';
import { cn } from '@/lib/ui/cn';
import { FieldFocus } from './deepLinkFocus';

/** Join a parent dot-path with a child segment (object key or array index). */
function joinPath(parent: string, segment: string | number): string {
  return parent ? `${parent}.${segment}` : String(segment);
}

/**
 * Schema-driven form (plan §4.7 — the keystone). Controlled: renders a JSON-Schema object
 * into Almanac inputs and emits the bounded `data` object. Block field lists are NEVER
 * hardcoded; adding a backend block type needs no console change (spec §9.1).
 *
 * `translateOnly` (spec §6.3) makes EN read-only and only AR editable across I18n fields.
 */
export function SchemaForm({
  schema,
  value,
  onChange,
  errors,
  translateOnly,
  disabled,
  defs,
  path = '',
}: {
  schema: BlockSchema | JSONSchemaNode;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  translateOnly?: boolean;
  disabled?: boolean;
  defs?: Record<string, JSONSchemaNode>;
  /** Dot-path prefix for this form's fields, used for search deep-link focus. */
  path?: string;
}) {
  const { t } = useTranslation();
  const allDefs = { ...(schema as BlockSchema).$defs, ...defs };
  const fields = resolveFields(schema, allDefs);

  const setField = (name: string, v: unknown) => onChange({ ...value, [name]: v });
  // Atomic multi-key write — used by ref-slug fields that also sync a sibling id key.
  const setPair = (partial: Record<string, unknown>) => onChange({ ...value, ...partial });

  // An object schema with no described properties (e.g. an under-specified block) would render
  // an empty container. Show an explicit empty-state instead of a confusing blank area (FE-004).
  if (fields.length === 0) {
    return <p className="text-small text-ink-mute">{t('forms.noFields')}</p>;
  }

  return (
    <div className="flex flex-col gap-md">
      {fields.map((f) => {
        const fieldPath = joinPath(path, f.name);
        return (
          <FieldFocus key={f.name} path={fieldPath}>
            <FieldRenderer
              field={f}
              path={fieldPath}
              value={value[f.name]}
              onChange={(v) => setField(f.name, v)}
              onCommitPair={setPair}
              error={errors?.[f.name]}
              translateOnly={translateOnly}
              disabled={disabled}
              defs={allDefs}
            />
          </FieldFocus>
        );
      })}
    </div>
  );
}

function FieldRenderer({
  field,
  value,
  onChange,
  onCommitPair,
  error,
  translateOnly,
  disabled,
  defs,
  path = '',
}: {
  field: ResolvedField;
  value: unknown;
  onChange: (v: unknown) => void;
  /** Atomic multi-key write (ref-slug id sync); absent for array items. */
  onCommitPair?: (partial: Record<string, unknown>) => void;
  error?: string;
  translateOnly?: boolean;
  disabled?: boolean;
  defs: Record<string, JSONSchemaNode>;
  /** This field's dot-path, threaded into containers for search deep-link focus. */
  path?: string;
}) {
  const { t } = useTranslation();
  const { widget, required, schema, entity, name } = field;
  // Resolve label through i18n: if `label` is a catalog key (e.g. set by config
  // owners to `fields.heroImage`) it is translated; a plain English string like
  // "Item 1" or "Copy" renders unchanged via defaultValue.
  const label = t(field.label, { defaultValue: field.label });
  const hint = schema['x-hint'] ? t(schema['x-hint'], { defaultValue: schema['x-hint'] }) : undefined;
  // In translate-only mode, non-localized fields are simply read-only.
  const nonI18nDisabled =
    disabled ||
    (translateOnly &&
      widget !== 'i18n' &&
      widget !== 'i18n-rich' &&
      widget !== 'object' &&
      widget !== 'array' &&
      widget !== 'map');

  switch (widget) {
    case 'i18n':
    case 'i18n-rich':
      return (
        <I18nField
          label={label}
          value={value as I18n | undefined}
          onChange={onChange}
          required={required}
          error={error}
          multiline={widget === 'i18n-rich'}
          translateOnly={translateOnly}
          disabled={disabled}
        />
      );
    case 'media':
      return (
        <MediaPickerField
          label={label}
          value={value as string | null}
          onChange={onChange}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
        />
      );
    case 'entity':
      return (
        <EntityPickerField
          label={label}
          collection={entity ?? 'industries'}
          value={value as string | null}
          onChange={onChange}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
        />
      );
    case 'ref-slug':
      return (
        <ReferenceSelectField
          label={label}
          collection={schema['x-ref-slug'] ?? 'industries'}
          value={value as string | null}
          onChange={(slug, item) => {
            const idField = schema['x-ref-id-field'];
            if (idField && onCommitPair) onCommitPair({ [name]: slug, [idField]: item?.id ?? null });
            else onChange(slug);
          }}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
          hint={schema['x-hint']}
        />
      );
    case 'enum': {
      const enumLabels = schema['x-enum-labels'] ?? {};
      return (
        <Select
          label={label}
          value={(value as string) ?? undefined}
          onValueChange={onChange}
          options={(schema.enum ?? []).map((e) => ({
            value: e,
            label: t(enumLabels[e] ?? e, { defaultValue: e }),
          }))}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
        />
      );
    }
    case 'boolean':
      return (
        <Switch
          label={label}
          hint={hint}
          checked={!!value}
          onCheckedChange={onChange}
          disabled={nonI18nDisabled}
        />
      );
    case 'number':
      return (
        <TextInput
          type="number"
          label={label}
          hint={hint}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'string': {
      const long = (schema.description ?? '').length > 60;
      const Comp = long ? TextArea : TextInput;
      return (
        <Comp
          label={label}
          hint={hint}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
          value={(value as string) ?? ''}
          onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
        />
      );
    }
    case 'geo':
      return (
        <GeoPickerField
          label={label}
          hint={schema['x-hint']}
          value={value as { lat?: number; lng?: number } | undefined}
          onChange={onChange}
          required={required}
          error={error}
          disabled={nonI18nDisabled}
        />
      );
    case 'object':
      return (
        <NestedObject
          label={label}
          schema={schema}
          value={(value as Record<string, unknown>) ?? {}}
          onChange={onChange}
          translateOnly={translateOnly}
          disabled={disabled}
          defs={defs}
          path={path}
        />
      );
    case 'array':
      return (
        <RepeatableArray
          label={label}
          schema={schema}
          value={(value as unknown[]) ?? []}
          onChange={onChange}
          translateOnly={translateOnly}
          disabled={disabled}
          defs={defs}
          path={path}
        />
      );
    case 'map':
      return (
        <MapField
          label={label}
          schema={schema}
          value={(value as Record<string, unknown>) ?? {}}
          onChange={onChange}
          translateOnly={translateOnly}
          disabled={disabled}
          defs={defs}
          path={path}
        />
      );
    default:
      // Guarded raw-JSON fallback so no field is ever unmanageable (spec §9.3).
      return (
        <RawJsonField
          label={t('forms.rawLabel', { name, defaultValue: `${name} (raw)` })}
          value={value}
          onChange={onChange}
          error={error}
        />
      );
  }
}

function NestedObject({
  label,
  schema,
  value,
  onChange,
  translateOnly,
  disabled,
  defs,
  path = '',
}: {
  label: string;
  schema: JSONSchemaNode;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  translateOnly?: boolean;
  disabled?: boolean;
  defs: Record<string, JSONSchemaNode>;
  path?: string;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="rounded-md border-[0.5px] border-hairline p-md">
      <legend className="eyebrow px-xs text-ink-mute">{t(label, { defaultValue: label })}</legend>
      <SchemaForm
        schema={schema}
        value={value}
        onChange={onChange}
        translateOnly={translateOnly}
        disabled={disabled}
        defs={defs}
        path={path}
      />
    </fieldset>
  );
}

function RepeatableArray({
  label,
  schema,
  value,
  onChange,
  translateOnly,
  disabled,
  defs,
  path = '',
}: {
  label: string;
  schema: JSONSchemaNode;
  value: unknown[];
  onChange: (v: unknown[]) => void;
  translateOnly?: boolean;
  disabled?: boolean;
  defs: Record<string, JSONSchemaNode>;
  path?: string;
}) {
  const { t } = useTranslation();
  const itemSchema = schema.items ?? { type: 'string' };
  const add = () => onChange([...value, emptyValue(itemSchema, defs)]);
  const removeAt = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const setAt = (i: number, v: unknown) => onChange(value.map((x, idx) => (idx === i ? v : x)));

  return (
    <fieldset className="rounded-md border-[0.5px] border-hairline p-md">
      <legend className="eyebrow px-xs text-ink-mute">{t(label, { defaultValue: label })}</legend>
      <div className="flex flex-col gap-sm">
        {value.map((item, i) => (
          <FieldFocus key={i} path={joinPath(path, i)}>
            <div className={cn('rounded-sm border-[0.5px] border-hairline bg-canvas-bone p-sm')}>
              <div className="mb-xs flex items-center justify-between">
                <span className="font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
                  {t('forms.indexLabel', { n: i + 1 })}
                </span>
                {!disabled && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeAt(i)}>
                    {t('forms.remove')}
                  </Button>
                )}
              </div>
              <FieldRenderer
                field={{
                  name: `${label}[${i}]`,
                  schema: itemSchema,
                  widget: detectItemWidget(itemSchema),
                  label: t('forms.item', { n: i + 1 }),
                  required: false,
                  entity: itemSchema['x-entity'],
                }}
                path={joinPath(path, i)}
                value={item}
                onChange={(v) => setAt(i, v)}
                translateOnly={translateOnly}
                disabled={disabled}
                defs={defs}
              />
            </div>
          </FieldFocus>
        ))}
        {!disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={add}>
            {t('forms.addItem')}
          </Button>
        )}
      </div>
    </fieldset>
  );
}

function detectItemWidget(node: JSONSchemaNode) {
  // Lazy import avoidance: reuse detectWidget via require-like pattern not available; inline.
  if (node.$ref && /I18nRich$/.test(node.$ref)) return 'i18n-rich' as const;
  if (node.$ref && /I18n$/.test(node.$ref)) return 'i18n' as const;
  if (node['x-ref-slug']) return 'ref-slug' as const;
  if (node.format === 'uuid') return node['x-entity'] ? ('entity' as const) : ('media' as const);
  if (node.enum) return 'enum' as const;
  if (node.type === 'object') {
    const ap = node.additionalProperties;
    const hasProps = !!node.properties && Object.keys(node.properties).length > 0;
    if (!hasProps && typeof ap === 'object' && ap !== null) return 'map' as const;
    return 'object' as const;
  }
  if (node.type === 'array') return 'array' as const;
  if (node.type === 'boolean') return 'boolean' as const;
  if (node.type === 'number' || node.type === 'integer') return 'number' as const;
  return 'string' as const;
}

/**
 * Dynamic-key map editor: rows of (key, value) where every value conforms to the
 * map's `additionalProperties` schema. Keys are author-named (e.g. a family's
 * variant slugs), so the row order and key text are held in local state — editing
 * a key char-by-char must not collapse the entry on an interim empty/duplicate key
 * or steal focus. The object emitted to the parent is rebuilt from non-empty keys.
 */
function MapField({
  label,
  schema,
  value,
  onChange,
  translateOnly,
  disabled,
  defs,
  path = '',
}: {
  label: string;
  schema: JSONSchemaNode;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  translateOnly?: boolean;
  disabled?: boolean;
  defs: Record<string, JSONSchemaNode>;
  path?: string;
}) {
  const { t } = useTranslation();
  const valueSchema = mapValueSchema(schema);
  const [entries, setEntries] = useState<Array<{ id: number; k: string; v: unknown }>>(() =>
    Object.entries(value).map(([k, v], i) => ({ id: i, k, v })),
  );
  const nextId = useRef(entries.length);

  const commit = (next: Array<{ id: number; k: string; v: unknown }>) => {
    setEntries(next);
    const out: Record<string, unknown> = {};
    for (const e of next) {
      const key = e.k.trim();
      if (key) out[key] = e.v;
    }
    onChange(out);
  };

  const dupKeys = new Set(
    entries
      .map((e) => e.k.trim())
      .filter((k, i, arr) => k && arr.indexOf(k) !== i),
  );

  return (
    <fieldset className="rounded-md border-[0.5px] border-hairline p-md">
      <legend className="eyebrow px-xs text-ink-mute">{t(label, { defaultValue: label })}</legend>
      <div className="flex flex-col gap-sm">
        {entries.map((e) => (
          <FieldFocus key={e.id} path={joinPath(path, e.k.trim())}>
          <div className="rounded-sm border-[0.5px] border-hairline bg-canvas-bone p-sm">
            <div className="mb-xs flex items-end justify-between gap-sm">
              <div className="flex-1">
                <TextInput
                  label={t('forms.mapKey', { defaultValue: 'Key' })}
                  value={e.k}
                  disabled={disabled}
                  error={dupKeys.has(e.k.trim()) ? t('forms.mapKeyDuplicate', { defaultValue: 'Duplicate key' }) : undefined}
                  onChange={(ev) =>
                    commit(entries.map((x) => (x.id === e.id ? { ...x, k: ev.target.value } : x)))
                  }
                />
              </div>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => commit(entries.filter((x) => x.id !== e.id))}
                >
                  {t('forms.remove')}
                </Button>
              )}
            </div>
            <FieldRenderer
              field={{
                name: `${label}.${e.k || '(key)'}`,
                schema: valueSchema,
                widget: detectItemWidget(valueSchema),
                label: e.k || t('forms.mapValue', { defaultValue: 'Value' }),
                required: false,
                entity: valueSchema['x-entity'],
              }}
              path={joinPath(path, e.k.trim())}
              value={e.v}
              onChange={(v) => commit(entries.map((x) => (x.id === e.id ? { ...x, v } : x)))}
              translateOnly={translateOnly}
              disabled={disabled}
              defs={defs}
            />
          </div>
          </FieldFocus>
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const id = nextId.current++;
              commit([...entries, { id, k: '', v: emptyValue(valueSchema, defs) }]);
            }}
          >
            {t('forms.addEntry', { defaultValue: 'Add entry' })}
          </Button>
        )}
      </div>
    </fieldset>
  );
}

export function RawJsonField({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}) {
  const { t } = useTranslation();
  let text: string;
  try {
    text = JSON.stringify(value ?? null, null, 2);
  } catch {
    text = '';
  }
  return (
    <TextArea
      label={t(label, { defaultValue: label })}
      defaultValue={text}
      error={error}
      className="font-mono"
      onBlur={(e) => {
        try {
          onChange(JSON.parse(e.target.value));
        } catch {
          /* keep last valid; surfaced as a validation error on submit */
        }
      }}
      hint={t('forms.rawHint')}
    />
  );
}
